// components/RecycleBin.jsx
import React, { useEffect, useMemo, useState } from 'react';
import apiClient from '../config/api';
import Navbar from './Navbar';
import { styles } from '../styles/newstyles';

/** Recycle Bin view with multi-select + bulk delete
 * Endpoints used (unchanged from your app):
 *  GET    /api/auth/session
 *  GET    /api/files/uploads/recyclebin           -> [{ FileID, DeletedAt, ... }]
 *  PATCH  /api/files/uploads/:id/restore
 *  DELETE /api/files/uploads/:id/permanent
 * Optional (faster): DELETE /api/files/uploads/bulk (body: { ids })
 */

const RecycleBin = ({ onLogout }) => {
  // --- Navbar/session state ---
  const [username, setUsername] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);

  // --- Data + UI state ---
  const [deletedFiles, setDeletedFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [reasonModal, setReasonModal] = useState(null);
  const [descriptionModal, setDescriptionModal] = useState(null);

  // --- Multi-select state ---
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkMode, setBulkMode] = useState(false);
  const hasSelection = selectedIds.size > 0;

  const allIds = useMemo(() => deletedFiles.map((f) => f.FileID), [deletedFiles]);
  const selectAllChecked = useMemo(
    () => allIds.length > 0 && allIds.every((id) => selectedIds.has(id)),
    [allIds, selectedIds]
  );

  useEffect(() => {
    if (!bulkMode) setSelectedIds(new Set());
  }, [bulkMode]);

  useEffect(() => {
    const load = async () => {
      try {
        const sess = await apiClient.get('/auth/session', { withCredentials: true });
        if (sess.data?.username) setUsername(sess.data.username);
        if (sess.data?.isAdmin) setIsAdmin(true);
      } catch {
        /* non-fatal */
      }
      await fetchDeletedFiles();
    };
    load();
  }, []);

  const fetchDeletedFiles = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/files/uploads/recyclebin', {
        withCredentials: true,
      });
      const files = res.data?.files || [];
      setDeletedFiles(files);
      setSelectedIds(new Set()); // clear selection on refresh
      setBulkMode(false);
    } catch (err) {
      console.error('Error fetching deleted files:', err);
      setMessage('Failed to load deleted files.');
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (fileId) => {
    try {
      await apiClient.patch(
        `/files/uploads/${fileId}/restore`,
        {},
        { withCredentials: true }
      );
      setDeletedFiles((prev) => prev.filter((file) => file.FileID !== fileId));
      setSelectedIds((prev) => {
        const next = new Set(prev); next.delete(fileId); return next;
      });
      setMessage('File restored successfully.');
    } catch (err) {
      console.error('Restore failed:', err);
      setMessage('Failed to restore file.');
    }
  };

  const handlePermanentDelete = async (fileId) => {
    if (!window.confirm('Are you sure you want to permanently delete this file?')) return;
    try {
      await apiClient.delete(`/files/uploads/${fileId}/permanent`, {
        withCredentials: true,
      });
      setDeletedFiles((prev) => prev.filter((file) => file.FileID !== fileId));
      setSelectedIds((prev) => {
        const next = new Set(prev); next.delete(fileId); return next;
      });
      setMessage('File deleted permanently.');
    } catch (err) {
      console.error('Permanent delete failed:', err);
      setMessage('Failed to permanently delete file.');
    }
  };

  // ---- Bulk delete ----
  const handleBulkDelete = async () => {
    if (!hasSelection) return;
    const ids = Array.from(selectedIds);
    const confirmed = window.confirm(
      `Permanently delete ${ids.length} item${ids.length > 1 ? 's' : ''}? This cannot be undone.`
    );
    if (!confirmed) return;

    setLoading(true);
    try {
      // Try optional bulk route first
      const bulk = await apiClient.delete('/files/uploads/bulk', {
        withCredentials: true,
        data: { ids },
        validateStatus: () => true,
      });

      if (bulk.status === 200) {
        setDeletedFiles((prev) => prev.filter((f) => !selectedIds.has(f.FileID)));
        setSelectedIds(new Set());
        setBulkMode(false);
        setMessage('Selected files deleted permanently.');
      } else if (bulk.status === 404) {
        // Fallback to sequential deletes
        await Promise.allSettled(
          ids.map((id) =>
            apiClient.delete(`/files/uploads/${id}/permanent`, {
              withCredentials: true,
            })
          )
        );
        setDeletedFiles((prev) => prev.filter((f) => !selectedIds.has(f.FileID)));
        setSelectedIds(new Set());
        setBulkMode(false);
        setMessage('Selected files deleted permanently.');
      } else {
        setMessage(`Bulk delete failed: ${bulk.statusText || bulk.status}`);
      }
    } catch (e) {
      console.error('Bulk delete error:', e);
      setMessage('Network error while deleting selected files.');
    } finally {
      setLoading(false);
    }
  };

  // ---- Selection helpers ----
  const toggleOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    if (selectAllChecked) setSelectedIds(new Set());
    else setSelectedIds(new Set(allIds));
  };

  const selectedCountLabel = useMemo(() => {
    const n = selectedIds.size;
    return n ? `${n} selected` : '0 selected';
  }, [selectedIds]);

  const formatDate = (raw) => {
    if (!raw) return 'N/A';

    // Normalize: handle ISO "T", strip milliseconds, default missing time to 00:00:00
    const normalized = String(raw).trim().replace('T', ' ');
    const [datePart, timeWithMs = '00:00:00'] = normalized.split(' ');
    if (!datePart) return 'N/A';

    const timePart = timeWithMs.split('.')[0] || '00:00:00';
    const [year, month, day] = datePart.split('-').map((v) => parseInt(v, 10));
    const [hourRaw = '0', minuteRaw = '0', secondRaw = '0'] = timePart.split(':');

    if ([year, month, day].some(Number.isNaN)) return 'N/A';

    const hour24 = parseInt(hourRaw, 10);
    const minute = parseInt(minuteRaw, 10) || 0;
    const second = parseInt(secondRaw, 10) || 0;
    if (Number.isNaN(hour24)) return 'N/A';

    const ampm = hour24 >= 12 ? 'PM' : 'AM';
    const hour12 = ((hour24 + 11) % 12) + 1;
    const pad = (val) => String(val).padStart(2, '0');

    return `${pad(month)}/${pad(day)}/${String(year).slice(-2)} ${hour12}:${pad(minute)}:${pad(second)} ${ampm}`;
  };

  const truncateWords = (text = '', numWords = 5) => {
    const words = text.trim().split(/\s+/);
    return words.length > numWords ? `${words.slice(0, numWords).join(' ')}...` : text;
  };

  // --- Recycle-Bin-specific success style (replaces styles.success) ---
  const rb = {
    banner: {
      margin: '14px 20px 0 20px',
      padding: '14px 16px',
      borderRadius: 14,
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      background: 'linear-gradient(180deg, #e9f7ef 0%, #e8f4ff 100%)', // soft green -> page blue
      border: '1px solid #cfe9db',
      boxShadow: '0 6px 18px rgba(13, 60, 97, 0.08)',
    },
    tick: {
      flex: '0 0 auto',
      width: 22,
      height: 22,
      borderRadius: '50%',
      background: '#2e7d32',
      display: 'grid',
      placeItems: 'center',
      color: 'white',
      fontSize: 14,
      fontWeight: 700,
    },
    text: {
      color: '#1b5e20',
      fontWeight: 600,
      letterSpacing: 0.2,
    },
    toolbar: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '10px 20px',
    },
    left: { display: 'flex', alignItems: 'center', gap: 10 },
    count: { fontSize: '0.95rem', opacity: 0.85 },
    bulkToggleBtn: {
      ...styles.viewButton,
      fontWeight: 600,
      padding: '8px 18px',
    },
    refreshBtn: {
      ...styles.viewButton,
      padding: '8px 14px',
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
    },
    cancelBulkBtn: {
      ...styles.viewButton,
      backgroundColor: '#f0f4f8',
      color: '#102a43',
      border: '1px solid #d9e2ec',
    },
    bulkDeleteBtn: {
      ...styles.deleteButton,
      opacity: hasSelection ? 1 : 0.6,
      cursor: hasSelection ? 'pointer' : 'not-allowed',
    },
    checkboxCol: { width: 36, textAlign: 'center' },
  };

  return (
    <div style={{ ...styles.mainContainer }}>
      <Navbar username={username} onLogout={onLogout} isAdmin={isAdmin} />

      <div style={styles.recycleHeader}>
        <h2 style={styles.heading}>Recycle Bin</h2>
      </div>

      {/* Bulk toolbar */}
      <div style={rb.toolbar}>
        <div style={rb.left}>
          {!bulkMode ? (
            <button
              type="button"
              style={rb.bulkToggleBtn}
              onClick={() => setBulkMode(true)}
              disabled={!deletedFiles.length}
            >
              Bulk Delete
            </button>
          ) : (
            <>
              <input
                type="checkbox"
                checked={selectAllChecked}
                onChange={toggleAll}
                aria-label="Select all"
              />
              <span style={rb.count}>{selectedCountLabel}</span>
              <button
                type="button"
                style={rb.cancelBulkBtn}
                onClick={() => setBulkMode(false)}
              >
                Cancel
              </button>
            </>
          )}
          <button
            type="button"
            style={rb.refreshBtn}
            onClick={fetchDeletedFiles}
            disabled={loading}
            title="Reload deleted files"
            aria-label="Refresh deleted files"
          >
            <span
              aria-hidden="true"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="23 4 23 10 17 10" />
                <polyline points="1 20 1 14 7 14" />
                <path d="M3.51 9a9 9 0 0114.13-3.36L23 10M1 14l5.36 4.36A9 9 0 0019.5 15" />
              </svg>
              {loading && <span style={{ fontSize: '11px' }}>...</span>}
            </span>
          </button>
        </div>
        {bulkMode && (
          <button
            style={rb.bulkDeleteBtn}
            onClick={handleBulkDelete}
            disabled={!hasSelection || loading}
            title={hasSelection ? 'Permanently delete selected' : 'Select items to delete'}
          >
            Delete Selected
          </button>
        )}
      </div>

      {/* Recycle-Bin-specific success banner */}
      {message && (
        <div role="status" aria-live="polite" style={rb.banner}>
          <span style={rb.tick}>✓</span>
          <span style={rb.text}>{message}</span>
        </div>
      )}

      {loading ? (
        <p style={{ margin: '12px 20px' }}>Loading deleted files...</p>
      ) : deletedFiles.length === 0 ? (
        <div style={styles.noRecordsContainer}>
          <p style={styles.noRecordsMessage}>Recycle Bin is empty.</p>
        </div>
      ) : (
        <div style={styles.tableContainer}>
          <table style={styles.table}>
            <thead>
              <tr>
                {bulkMode && (
                  <th style={{ ...styles.th, ...rb.checkboxCol }}>
                    <input
                      type="checkbox"
                      checked={selectAllChecked}
                      onChange={toggleAll}
                      aria-label="Select all"
                    />
                  </th>
                )}
                <th style={styles.th}>FILE ID</th>
                <th style={styles.th}>DATE DELETED</th>
                <th style={styles.th}>CATEGORY</th>
                <th style={styles.th}>TYPE</th>
                <th style={styles.th}>DESCRIPTION</th>
                <th style={styles.th}>COUNTY</th>
                <th style={styles.th}>REASON</th>
                <th style={styles.th}>UPLOADED BY</th>
                <th style={styles.th}>DELETED BY</th>
                <th style={styles.th}>RESTORE</th>
                <th style={styles.th}>PERMANENT DELETE</th>
              </tr>
            </thead>
            <tbody>
              {deletedFiles.map((file, idx) => (
                <tr key={file.FileID ?? idx}>
                  {bulkMode && (
                    <td style={{ ...styles.td, ...rb.checkboxCol }}>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(file.FileID)}
                        onChange={() => toggleOne(file.FileID)}
                        aria-label={`Select ${file.FileID}`}
                      />
                    </td>
                  )}
                  <td style={styles.td}>{file.FileID}</td>
                  <td style={styles.td}>{formatDate(file.DeletedAt)}</td>
                  <td style={styles.td}>{file.CategoryName || '—'}</td>
                  <td style={styles.td}>{file.TypeName || '—'}</td>
                  <td style={styles.td}>
                    {file.Description ? (
                      <>
                        <span>{truncateWords(file.Description, 4)} </span>
                        {file.Description.trim().split(/\s+/).length > 4 && (
                          <button
                            type="button"
                            onClick={() => setDescriptionModal(file.Description)}
                            style={{
                              background: 'none',
                              border: 'none',
                              padding: 0,
                              margin: 0,
                              color: '#0ea5e9',
                              cursor: 'pointer',
                              fontWeight: 700,
                              fontSize: '12px',
                              textDecoration: 'underline'
                            }}
                          >
                            Show more
                          </button>
                        )}
                      </>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td style={styles.td}>{file.CountyName || '—'}</td>
                  <td style={styles.td}>
                    {file.DeleteReason ? (
                      <>
                        <span>{truncateWords(file.DeleteReason, 4)} </span>
                        {file.DeleteReason.trim().split(/\s+/).length > 4 && (
                          <button
                            type="button"
                            onClick={() => setReasonModal(file.DeleteReason)}
                            style={{
                              background: 'none',
                              border: 'none',
                              padding: 0,
                              margin: 0,
                              color: '#0ea5e9',
                              cursor: 'pointer',
                              fontWeight: 700,
                              fontSize: '12px',
                              textDecoration: 'underline'
                            }}
                          >
                            Show more
                          </button>
                        )}
                      </>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td style={styles.td}>{file.UploadedBy || '—'}</td>
                  <td style={styles.td}>{file.DeletedByUsername || '—'}</td>
                  <td style={styles.td}>
                    <button onClick={() => handleRestore(file.FileID)} style={styles.viewButton}>
                      Restore
                    </button>
                  </td>
                  <td style={styles.td}>
                    <button
                      onClick={() => handlePermanentDelete(file.FileID)}
                      style={styles.deleteButton}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {reasonModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.55)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1200
          }}
        >
          <div
            style={{
              background: '#fffaf3',
              padding: '24px',
              borderRadius: '14px',
              maxWidth: '520px',
              width: '90%',
              boxShadow: '0 18px 40px rgba(0,0,0,0.18)',
              border: '1px solid rgba(141,110,78,0.2)'
            }}
          >
            <h4 style={{ marginTop: 0, marginBottom: 12, color: '#3d2817' }}>Delete Reason</h4>
            <p style={{ margin: 0, color: '#3d2817', lineHeight: 1.5, wordBreak: 'break-word' }}>{reasonModal}</p>
            <div style={{ textAlign: 'right', marginTop: 16 }}>
              <button
                type="button"
                onClick={() => setReasonModal(null)}
                style={{ ...styles.viewButton, padding: '8px 16px' }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {descriptionModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.55)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1200
          }}
        >
          <div
            style={{
              background: '#fffaf3',
              padding: '24px',
              borderRadius: '14px',
              maxWidth: '520px',
              width: '90%',
              boxShadow: '0 18px 40px rgba(0,0,0,0.18)',
              border: '1px solid rgba(141,110,78,0.2)'
            }}
          >
            <h4 style={{ marginTop: 0, marginBottom: 12, color: '#3d2817' }}>Description</h4>
            <p style={{ margin: 0, color: '#3d2817', lineHeight: 1.5, wordBreak: 'break-word' }}>{descriptionModal}</p>
            <div style={{ textAlign: 'right', marginTop: 16 }}>
              <button
                type="button"
                onClick={() => setDescriptionModal(null)}
                style={{ ...styles.viewButton, padding: '8px 16px' }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecycleBin;
